import React, { useEffect, useState } from "react";
import { formatDate } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { updateItem, getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import { sequenceForSide, allEmailIds, countSent, emailStatus } from "../../utils/transactionEmailSequence";
import {
  createCustomTemplate,
  saveEmailTemplate,
  loadEmailTemplates,
  mergedCategoryList,
} from "../../utils/emailTemplateLoader";
import {
  cleanPastedText as cleanPasted,
  cleanPastedSubject,
} from "../../utils/emailBrandTemplate";
import { BASE_CATEGORY_ORDER } from "../settings/email-templates-picker";
import { getSentEmailsForDeal } from "../../utils/sentEmails";
import EmailComposer from "../email/EmailComposer";

// Template bodies/subjects are stored as plain text. Anything that still
// holds raw HTML (old Gmail pastes, composer "Save as Template" rich text) is
// converted to clean readable text — links kept as "text (URL)". Plain text
// is untouched. cleanPasted (body/multi-line) vs cleanPastedSubject
// (single-line, collapses newlines) are the same shared helpers Settings →
// Email Templates uses, so subjects clean identically everywhere.

const ONE_OFF = { id: "one-off", subject: "", body: "", recipientLabel: "Anyone", recipientRole: "client" };

const fmtDate = (ms) => {
  if (!ms) return "";
  try {
    return formatDate(ms);
  } catch {
    return "";
  }
};

export default function EmailCenterTab({ deal, onDealUpdate }) {
  const navigate = useNavigate();
  const [{ user, emailTemplates }, dispatch] = useStateValue();
  const [contacts, setContacts] = useState([]);
  const [openEmail, setOpenEmail] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewingSent, setViewingSent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = Array.isArray(deal?.customers) ? deal.customers.filter(Boolean) : [];
      if (!ids.length) return setContacts([]);
      try {
        const raws = await getContactsByIds(ids);
        if (!cancelled) setContacts((raws || []).map(normalizeContact));
      } catch (e) {
        console.error("EmailCenterTab contacts:", e);
        if (!cancelled) setContacts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deal?.id, deal?.customers]);

  const refreshHistory = React.useCallback(async () => {
    try {
      setHistory(await getSentEmailsForDeal(String(deal?.id || deal?.createdat || "")));
    } catch (e) {
      console.error("EmailCenterTab history:", e);
    }
  }, [deal?.id, deal?.createdat]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  const milestones = sequenceForSide(deal?.dealtype);
  const total = allEmailIds(deal?.dealtype).length;
  const sentCount = countSent(deal, allEmailIds(deal?.dealtype));

  // Layer any saved Settings → Email Templates customization (subject/body/
  // default video) on top of the hardcoded sequence default, keyed by id.
  // Empty-string overrides are ignored — a cleared subject in Settings must
  // never blank out an email's title here.
  const resolve = (email) => {
    const override = emailTemplates?.[email.id] || {};
    const clean = Object.fromEntries(
      Object.entries(override).filter(([, v]) => v !== "" && v != null),
    );
    // Old overrides may hold raw pasted HTML — compose from readable text.
    if (typeof clean.subject === "string") clean.subject = cleanPastedSubject(clean.subject);
    if (typeof clean.body === "string") clean.body = cleanPasted(clean.body);
    return { ...email, ...clean };
  };

  const fillAddr = (s) =>
    String(s || "").replaceAll("{{propertyAddress}}", deal?.propertyaddress || "the property");

  // Flat ordered list of the sequence's emails (resolved), for pills + lookup.
  const flat = milestones.flatMap((m) =>
    m.emails.map((e) => ({ milestone: m.label, ...resolve(e) })),
  );

  // Custom templates (created in Settings → Email Templates) — sendable on any
  // deal as a one-off. Grouped by category for the dropdown.
  const customTemplates = Object.entries(emailTemplates || {})
    .filter(([, v]) => v && v.custom)
    .map(([id, v]) => ({
      id,
      label: v.label || "Untitled Template",
      category: (v.category || "Custom").trim() || "Custom",
      subject: cleanPastedSubject(v.subject || ""),
      body: cleanPasted(v.body || ""),
      video: v.video || null,
      recipientLabel: "Custom",
      recipientRole: "client",
    }));
  const customByCategory = customTemplates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});
  // Optgroup order: base categories first (in their fixed order), then any
  // user-created categories alphabetically — same order as the Settings picker.
  const customCategoryOrder = mergedCategoryList(BASE_CATEGORY_ORDER, emailTemplates)
    .filter((cat) => customByCategory[cat]);

  const persist = async (next) => {
    try {
      await updateItem("deals", deal.id || deal.createdat, { transactionEmails: next });
      onDealUpdate?.({ ...deal, transactionEmails: next });
    } catch (e) {
      console.error("Persist transaction email status failed:", e);
    }
  };

  const recordSent = (emailId, record) => {
    const te = deal?.transactionEmails || {};
    persist({ ...te, sent: { ...(te.sent || {}), [emailId]: record } });
    setOpenEmail(null);
    refreshHistory();
  };

  const toggleSkip = (emailId) => {
    const te = deal?.transactionEmails || {};
    const skipped = { ...(te.skipped || {}) };
    if (skipped[emailId]) delete skipped[emailId];
    else skipped[emailId] = true;
    persist({ ...te, skipped });
  };

  // Phase 1 recipient: client role → first contact with an email; other roles
  // blank (agent fills in the composer). Stage 2 resolves lender/title/etc.
  const defaultRecipientFor = (role) => {
    if (role === "client") {
      const c = (contacts || []).find((x) => x?.email);
      return { name: c?.name || c?.firstName || "", email: c?.email || "" };
    }
    return { name: "", email: "" };
  };

  // Save whatever the agent wrote in the composer as a reusable custom template
  // (lands in Settings → Email Templates and the "My Templates" dropdown groups).
  const saveAsTemplate = async ({ label, subject, body, video }) => {
    try {
      // The composer hands us Quill HTML — store the template as clean plain
      // text (links kept) so it reopens readable instead of as tag soup.
      const id = await createCustomTemplate(
        { label, category: "Custom", subject: cleanPastedSubject(subject), body: cleanPasted(body) },
        user.id,
      );
      if (video) await saveEmailTemplate(id, { video }, user.id);
      const fresh = await loadEmailTemplates();
      dispatch({ type: actionType.SET_EMAIL_TEMPLATES, payload: fresh });
      toast.success(`Saved "${label}" as a template.`);
    } catch (e) {
      console.error("Save as template failed:", e);
      toast.error(`Couldn't save template: ${e.message || "unknown"}`);
    }
  };

  const pick = (id) => {
    if (!id) return setOpenEmail(null);
    if (id === "one-off") return setOpenEmail({ ...ONE_OFF, openedAt: Date.now() });
    const custom = customTemplates.find((t) => t.id === id);
    if (custom) return setOpenEmail({ ...custom, openedAt: Date.now() });
    const email = flat.find((e) => e.id === id);
    if (email) setOpenEmail({ ...email, openedAt: Date.now() });
  };

  const selectedStatus =
    openEmail && openEmail.id !== "one-off" ? emailStatus(deal, openEmail.id) : null;

  const fmt = (ms) =>
    new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div className="space-y-4">
      {/* Control bar: template dropdown + progress pills + status/actions */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <select
          value={openEmail?.id || ""}
          onChange={(e) => pick(e.target.value)}
          className="text-sm font-semibold rounded-md border border-slate-300 px-3 py-2 bg-white max-w-full sm:max-w-[420px]"
        >
          <option value="">Choose an Email to Send…</option>
          {milestones.map((m) => (
            <optgroup key={m.key} label={m.label}>
              {m.emails.map((raw) => {
                const email = resolve(raw);
                const st = emailStatus(deal, email.id);
                const prefix =
                  st.state === "sent" ? "✓ " : st.state === "skipped" ? "⊘ " : "";
                return (
                  <option key={email.id} value={email.id}>
                    {prefix}
                    {email.recipientLabel}: {fillAddr(email.subject)}
                  </option>
                );
              })}
            </optgroup>
          ))}
          {customCategoryOrder.map((cat) => (
            <optgroup key={`custom-${cat}`} label={`${cat} (My Templates)`}>
              {customByCategory[cat].map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </optgroup>
          ))}
          <optgroup label="Anything Else">
            <option value="one-off">+ One-Off Email</option>
          </optgroup>
        </select>

        <button
          type="button"
          onClick={() => navigate("/dashboard/settings?tab=emailTemplates")}
          className="text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded px-2.5 py-1.5 hover:bg-sky-100"
        >
          Manage Templates ↗
        </button>

        {/* Progress pills — one per sequence email, click to open */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {flat.map((email) => {
            const st = emailStatus(deal, email.id);
            const active = openEmail?.id === email.id;
            const cls =
              st.state === "sent"
                ? "bg-green-500 border-green-500"
                : st.state === "skipped"
                  ? "bg-slate-300 border-slate-300"
                  : active
                    ? "bg-amber-400 border-amber-400"
                    : "bg-white border-slate-300";
            return (
              <button
                key={email.id}
                type="button"
                onClick={() => pick(email.id)}
                title={`${email.milestone} — ${fillAddr(email.subject)}${
                  st.state === "sent" ? ` (sent ${fmtDate(st.record?.sentAt)})` : st.state === "skipped" ? " (skipped)" : ""
                }`}
                className={`w-3.5 h-3.5 rounded-full border-2 transition ${cls} ${active ? "ring-2 ring-amber-200" : ""}`}
              />
            );
          })}
        </div>

        <span className="text-xs font-semibold text-slate-500">
          {sentCount} of {total} Sent
        </span>

        {selectedStatus && (
          <span className="flex items-center gap-2 ml-auto">
            {selectedStatus.state === "sent" ? (
              <span className="text-xs font-semibold text-green-600">
                ✓ Sent {fmtDate(selectedStatus.record?.sentAt)}
                {selectedStatus.record?.recipientName
                  ? ` to ${selectedStatus.record.recipientName}`
                  : ""}
              </span>
            ) : selectedStatus.state === "skipped" ? (
              <>
                <span className="text-xs text-slate-400">Skipped</span>
                <button
                  onClick={() => toggleSkip(openEmail.id)}
                  className="text-xs font-semibold text-sky-600"
                >
                  Undo
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  toggleSkip(openEmail.id);
                  setOpenEmail(null);
                }}
                className="text-xs font-semibold text-slate-500 border border-slate-300 rounded px-2.5 py-1"
              >
                Skip This Email
              </button>
            )}
          </span>
        )}
      </div>

      {openEmail ? (
        <EmailComposer
          key={`${openEmail.id}:${openEmail.openedAt || 0}`}
          deal={deal}
          contacts={contacts}
          sender={user}
          email={openEmail}
          defaultRecipient={defaultRecipientFor(openEmail.recipientRole)}
          onSent={(rec) => recordSent(openEmail.id, rec)}
          onCancel={() => setOpenEmail(null)}
          onSaveAsTemplate={saveAsTemplate}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-400">
          Pick an email from the dropdown above — green dots are sent, the next
          white dot is usually what's due.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <h3 className="font-semibold text-slate-900 text-sm mb-2">History</h3>
        {history.length === 0 ? (
          <p className="text-xs text-slate-400">Nothing sent from this deal yet.</p>
        ) : (
          history.map((h) => (
            <div
              key={h.id}
              className="flex items-center justify-between text-xs py-1.5 border-t first:border-t-0 border-slate-100"
            >
              <span className="truncate">✉️ {h.subject} → {h.recipientName || h.recipientEmail}</span>
              <span className="text-slate-400 whitespace-nowrap pl-3">
                {fmt(h.sentAt)} ·{" "}
                <button onClick={() => setViewingSent(h)} className="text-sky-600 font-semibold">
                  view
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      {viewingSent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setViewingSent(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold text-slate-900 text-sm truncate">{viewingSent.subject}</h3>
              <button onClick={() => setViewingSent(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <iframe title="Sent email" srcDoc={viewingSent.bodyHtml} className="w-full flex-1 min-h-[420px]" />
          </div>
        </div>
      )}
    </div>
  );
}
